'use client';

import { CopyPlus, Trash2 } from 'lucide-react';

import { TextIcon } from '@/components/icons/svgs';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

import { ShortcutTooltip, Tooltip } from '@/shared/components';
import { COPY } from '@/shared/constants/copies';
import { ISFormInputField } from '@/shared/services/forms/forms.types';
import { KeyboardKeys, getCommandKeyboardKey } from '@/shared/utils/navigation';

import { NON_EDITABLE_INPUT_FIELD_TYPES } from '@/utils/constants';

import AddFieldDropdown from '../../../features/form-editing/components/common/add-field-dropdown';
import { IAddFieldDropdownOption } from '../../../features/form-editing/types/form-editing-types';

type DisabledTooltip = {
	isRequiredTooltip: string;
	typeTooltip: string;
	deleteTooltip: string;
};

type FieldActionBarProps = {
	inputField: ISFormInputField;
	formId: string;
	netFocusedField: boolean;
	nonEditable?: boolean;
	isRequired: boolean;
	disabledTooltip?: DisabledTooltip;
	onUpdateIsRequired: (value: boolean) => void;
	onClickAddDescription: () => void;
	onClickCopy: () => void;
	onClickDelete: () => void;
	onChangeInputField: (updatedInputField: ISFormInputField) => void;
	fieldTypeOptions?: IAddFieldDropdownOption[];
	hideDescription?: boolean;
	disableShortcuts?: boolean;
};

function FieldActionBar({
	inputField,
	formId,
	netFocusedField,
	nonEditable,
	isRequired,
	disabledTooltip,
	onUpdateIsRequired,
	onClickAddDescription,
	onClickCopy,
	onClickDelete,
	onChangeInputField,
	fieldTypeOptions,
	hideDescription,
	disableShortcuts = false,
}: FieldActionBarProps) {
	const deleteButton = (
		<Button
			variant={'icon'}
			className="p-0"
			onClick={onClickDelete}
			disabled={nonEditable}
		>
			<Trash2 className="size-4" />
		</Button>
	);

	return (
		<div className="mt-4 flex w-full items-center gap-2 border-t border-border p-1 md:w-fit md:gap-4">
			<div className="mr-2 md:mr-12">
				<AddFieldDropdown
					disableShortcuts={disableShortcuts}
					onAddOrEditField={onChangeInputField}
					addFieldMode="EDIT"
					selectedInputField={inputField}
					staticFieldNameToDisplay={
						NON_EDITABLE_INPUT_FIELD_TYPES.includes(inputField.preDefinedInputType ?? '')
							? inputField.preDefinedInputType === 'EMAIL'
								? 'Email'
								: 'Short Text'
							: undefined
					}
					formId={formId}
					disabled={!netFocusedField}
					overrideTooltipContent={nonEditable ? disabledTooltip?.typeTooltip : undefined}
					fieldOptions={fieldTypeOptions}
				/>
			</div>

			<div className="ml-auto flex items-center gap-2 md:ml-0 md:gap-4">
				<ShortcutTooltip
					text="Required"
					keys={[getCommandKeyboardKey(), KeyboardKeys.Shift, KeyboardKeys.Slash]}
					hotKeyCallback={() => (nonEditable ? undefined : onUpdateIsRequired(!isRequired))}
					hotKeyOptions={{
						preventDefault: true,
						enableOnFormTags: true,
						enabled: netFocusedField && !disableShortcuts,
					}}
					hotKeyDependencies={[onUpdateIsRequired]}
					enableInAllScopes
					overrideTooltipContent={
						nonEditable
							? (disabledTooltip?.isRequiredTooltip ?? COPY.fields.fieldNotEditable)
							: undefined
					}
				>
					<div className="mr-1 flex items-center gap-1.5">
						<Checkbox
							id="input-field-required"
							checked={isRequired}
							onCheckedChange={onUpdateIsRequired}
							disabled={!netFocusedField}
						/>
						<label
							htmlFor="input-field-required"
							className="cursor-pointer text-sm font-medium text-secondary"
						>
							Required
						</label>
					</div>
				</ShortcutTooltip>

				{!hideDescription && (
					<ShortcutTooltip
						text="Edit Description"
						keys={[getCommandKeyboardKey(), KeyboardKeys.Shift, 'D']}
						hotKeyCallback={() => onClickAddDescription()}
						hotKeyOptions={{
							preventDefault: true,
							enableOnFormTags: true,
							enabled: netFocusedField && !disableShortcuts,
						}}
						hotKeyDependencies={[onClickAddDescription]}
						enableInAllScopes
					>
						<Button
							variant={'icon'}
							className="p-0"
							onClick={onClickAddDescription}
						>
							<TextIcon className="size-4" />
						</Button>
					</ShortcutTooltip>
				)}

				<ShortcutTooltip
					text="Duplicate"
					keys={[getCommandKeyboardKey(), 'D']}
					hotKeyCallback={() => onClickCopy()}
					hotKeyOptions={{
						enableOnFormTags: true,
						preventDefault: true,
						enabled: !disableShortcuts,
					}}
					hotKeyDependencies={[onClickCopy]}
					enableInAllScopes
				>
					<Button
						variant={'icon'}
						className="p-0"
						onClick={onClickCopy}
					>
						<CopyPlus className="size-4" />
					</Button>
				</ShortcutTooltip>

				{nonEditable ? (
					<Tooltip text={disabledTooltip?.deleteTooltip ?? COPY.fields.fieldNotDeletable}>
						<span>{deleteButton}</span>
					</Tooltip>
				) : (
					<ShortcutTooltip
						text="Delete"
						keys={[getCommandKeyboardKey(), KeyboardKeys.Shift, KeyboardKeys.Backspace]}
						hotKeyCallback={() => onClickDelete()}
						hotKeyOptions={{
							enableOnFormTags: true,
							preventDefault: true,
							enabled: !disableShortcuts,
						}}
						hotKeyDependencies={[onClickDelete]}
						enableInAllScopes
					>
						{deleteButton}
					</ShortcutTooltip>
				)}
			</div>
		</div>
	);
}

export type { DisabledTooltip };
export default FieldActionBar;
